import type { PluginSourceInfo } from "@magic-md/plugin-sdk";

export type PluginSourceKind = NonNullable<PluginSourceInfo["kind"]>;
export type PluginSourceFilter = "all" | PluginSourceKind;

export const PLUGIN_SOURCE_FILTERS = [
    "all",
    "local",
    "cloud-worker",
    "cloud-bundle",
] as const satisfies readonly PluginSourceFilter[];

export interface PluginSourceCount {
    all: number;
    local: number;
    "cloud-worker": number;
    "cloud-bundle": number;
}

export interface PluginListSummaryInput {
    version?: string;
    actionCount?: number;
    source?: PluginSourceInfo;
    description?: string;
}

export interface PluginSourceDetailRow {
    key: string;
    labelKey: string;
    value: string;
    href?: string;
    mono?: boolean;
}

type Translator = (key: string, params?: Record<string, unknown>) => string;

export function normalizePluginSourceKind(
    source: PluginSourceInfo | undefined,
): PluginSourceKind {
    return source?.kind ?? "local";
}

export function pluginSourceLabelKey(kind: PluginSourceKind): string {
    switch (kind) {
        case "cloud-worker":
            return "pluginSource.cloudWorker";
        case "cloud-bundle":
            return "pluginSource.cloudBundle";
        default:
            return "pluginSource.local";
    }
}

export function pluginSourceTooltipKey(kind: PluginSourceKind): string {
    switch (kind) {
        case "cloud-worker":
            return "pluginSource.cloudWorker.tooltip";
        case "cloud-bundle":
            return "pluginSource.cloudBundle.tooltip";
        default:
            return "pluginSource.local.tooltip";
    }
}

export function pluginSourceFilterLabelKey(filter: PluginSourceFilter): string {
    if (filter === "all") return "pluginSource.filter.all";
    return pluginSourceLabelKey(filter);
}

export function pluginMatchesSourceFilter(
    source: PluginSourceInfo | undefined,
    filter: PluginSourceFilter,
): boolean {
    return filter === "all" || normalizePluginSourceKind(source) === filter;
}

export function countPluginSources<T extends { source?: PluginSourceInfo }>(
    items: readonly T[],
): PluginSourceCount {
    const counts: PluginSourceCount = {
        all: items.length,
        local: 0,
        "cloud-worker": 0,
        "cloud-bundle": 0,
    };
    for (const item of items) {
        counts[normalizePluginSourceKind(item.source)] += 1;
    }
    return counts;
}

export function getPluginSourcePrimaryUrl(
    source: PluginSourceInfo | undefined,
): string | undefined {
    return (
        source?.manifestUrl ??
        source?.remoteUrl ??
        source?.invokeUrl ??
        source?.bundleBaseUrl ??
        source?.localPath ??
        source?.manifestPath ??
        source?.uiBundlePath ??
        source?.url
    );
}

export function getPluginSourceHost(
    source: PluginSourceInfo | undefined,
): string | undefined {
    const url = getPluginSourcePrimaryUrl(source);
    if (!url) return undefined;
    try {
        return new URL(url).host;
    } catch {
        return undefined;
    }
}

export function inferPluginSourceProvider(
    source: PluginSourceInfo | undefined,
): string | undefined {
    if (source?.provider) return source.provider;
    const host = getPluginSourceHost(source);
    if (host?.endsWith(".workers.dev")) return "Cloudflare Workers";
    if (host?.endsWith(".pages.dev")) return "Cloudflare Pages";
    if (normalizePluginSourceKind(source) !== "local") return "Cloudflare";
    return undefined;
}

export function getPluginSourceSearchTerms(
    source: PluginSourceInfo | undefined,
): string[] {
    const kind = normalizePluginSourceKind(source);
    return [
        kind,
        source?.label,
        source?.url,
        source?.version,
        source?.manifestUrl,
        source?.remoteUrl,
        source?.invokeUrl,
        source?.apiUrl,
        source?.publishUrl,
        source?.healthUrl,
        source?.smartIntakeProposeUrl,
        source?.smartIntakeBuildPlanUrl,
        source?.bundleBaseUrl,
        source?.localPath,
        source?.manifestPath,
        source?.uiBundlePath,
        getPluginSourceHost(source),
        inferPluginSourceProvider(source),
    ].filter((value): value is string => !!value);
}

export function buildPluginListSummary(
    input: PluginListSummaryInput,
    t: Translator,
): { text: string; title: string } {
    void t;
    return {
        text: input.description ?? "",
        title: input.description ?? "",
    };
}

export function formatPluginSourceInline(
    source: PluginSourceInfo | undefined,
    t: Translator,
): string {
    const kind = normalizePluginSourceKind(source);
    const label = t(pluginSourceLabelKey(kind));
    const host = getPluginSourceHost(source);
    return host ? `${label} · ${host}` : label;
}

export function getPluginSourceDetailRows(
    source: PluginSourceInfo | undefined,
    fallbackVersion?: string,
): PluginSourceDetailRow[] {
    const kind = normalizePluginSourceKind(source);
    const rows: PluginSourceDetailRow[] = [
        {
            key: "type",
            labelKey: "pluginSource.detail.type",
            value: pluginSourceLabelKey(kind),
        },
    ];

    const version = source?.version ?? fallbackVersion;
    if (version) {
        rows.push({
            key: "version",
            labelKey: "pluginSource.detail.deployedVersion",
            value: `v${version}`,
        });
    }

    const provider = inferPluginSourceProvider(source);
    if (provider) {
        rows.push({
            key: "provider",
            labelKey: "pluginSource.detail.provider",
            value: provider,
        });
    }

    pushUrlRow(rows, "manifestUrl", "pluginSource.detail.manifestUrl", source?.manifestUrl ?? source?.url);
    pushUrlRow(rows, "remoteUrl", "pluginSource.detail.remoteUrl", source?.remoteUrl);
    pushUrlRow(rows, "invokeUrl", "pluginSource.detail.invokeUrl", source?.invokeUrl);
    pushUrlRow(rows, "apiUrl", "pluginSource.detail.apiUrl", source?.apiUrl);
    pushUrlRow(rows, "publishUrl", "pluginSource.detail.publishUrl", source?.publishUrl);
    pushUrlRow(rows, "healthUrl", "pluginSource.detail.healthUrl", source?.healthUrl);
    pushUrlRow(
        rows,
        "smartIntakeProposeUrl",
        "pluginSource.detail.smartIntakeProposeUrl",
        source?.smartIntakeProposeUrl,
    );
    pushUrlRow(
        rows,
        "smartIntakeBuildPlanUrl",
        "pluginSource.detail.smartIntakeBuildPlanUrl",
        source?.smartIntakeBuildPlanUrl,
    );
    pushUrlRow(rows, "bundleBaseUrl", "pluginSource.detail.bundleBaseUrl", source?.bundleBaseUrl);
    if (kind === "local") {
        pushPathRow(
            rows,
            "pluginAddress",
            "pluginSource.detail.pluginAddress",
            source?.localPath ?? source?.manifestPath ?? source?.uiBundlePath ?? source?.url,
        );
    }

    const host = getPluginSourceHost(source);
    if (host) {
        rows.push({
            key: "host",
            labelKey: "pluginSource.detail.host",
            value: host,
            mono: true,
        });
    }

    return dedupeRows(rows);
}

function pushPathRow(
    rows: PluginSourceDetailRow[],
    key: string,
    labelKey: string,
    value: string | undefined,
): void {
    if (!value) return;
    rows.push({
        key,
        labelKey,
        value,
        href: isHttpUrl(value) ? value : undefined,
        mono: true,
    });
}

function pushUrlRow(
    rows: PluginSourceDetailRow[],
    key: string,
    labelKey: string,
    value: string | undefined,
): void {
    if (!value) return;
    rows.push({
        key,
        labelKey,
        value,
        href: isHttpUrl(value) ? value : undefined,
        mono: true,
    });
}

function isHttpUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
}

function dedupeRows(rows: PluginSourceDetailRow[]): PluginSourceDetailRow[] {
    const seen = new Set<string>();
    return rows.filter((row) => {
        const id = `${row.labelKey}:${row.value}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}
